import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, Input, Signal } from '@angular/core';
import { AuthUser } from '@core/domain/model/user/auth-user.model';
import { TranslateModule } from '@ngx-translate/core';
import { StateTagComponent } from '@shared/ui-components/state-tag/state-tag.component';
import { AcRoutingService } from '@core/services/routing/ac-routing.service';
import { MatMenuModule } from '@angular/material/menu';
import { filter, map, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Store } from '@ngrx/store';
import { StoreState } from '@store/store.state';
import { userSelectors } from '@store/selectors/user.selectors';
import { masterSelectors } from '@store/selectors/master.selectors';
import { StateTagType } from '@shared/ui-components/state-tag/models/tag.model';
import { Router, RouterLink } from '@angular/router';
import { IconComponent } from '@shared/ui-components/icon/icon.component';
import { CustomLinkDirective } from '@shared/directives/custom-link/custom-link.directive';
import { appSelectors } from '@store/selectors/app.selectors';
import { MatDialog } from '@angular/material/dialog';
import { ImpersonationDialogComponent } from '@core/components/header/impersonation-dialog/impersonation-dialog.component';
import { userActions } from '@store/actions/user.actions';
import { authActions } from '@store/actions/auth.actions';
import { ApiAnnouncementService } from '@core/api/services/notifications/api-announcement.service';
import { FloatingAlertsService } from '@shared/components/floating-alerts/data/floating-alerts.service';
import { LoadingIds } from '@shared/constants/loading';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AcDialogService } from '@core/services/dialog/ac-dialog.service';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'ac-profile-info',
  standalone: true,
  imports: [
    TranslateModule,
    StateTagComponent,
    MatMenuModule,
    RouterLink,
    IconComponent,
    CustomLinkDirective,
    MatProgressSpinnerModule,
    AsyncPipe,
  ],
  templateUrl: './profile-info.component.html',
  styleUrl: './profile-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileInfoComponent {
  @Input({ required: true }) user!: AuthUser;
  @Input({ transform: booleanAttribute }) sideNav = false;
  protected readonly _userManagementRoute = AcRoutingService.USER_MANAGEMENT_ROUTE();
  protected readonly _isSuperUserSig = this._store.selectSignal(userSelectors.isSuperUser);
  protected readonly _isAdminOrSuperUser = computed(() => {
    const isSuperUser = this._isSuperUserSig();
    const isAdmin = this._store.selectSignal(masterSelectors.isAdmin)();
    return isAdmin || isSuperUser;
  });
  protected readonly _adminStateTagSig: Signal<{ tag: StateTagType; desc: string }> = computed(() => {
    if (this._isAdminOrSuperUser()) {
      return {
        tag: 'admin',
        desc: 'USER_MANAGEMENT.ROLE.ADMIN',
      };
    } else {
      return {
        tag: 'user',
        desc: 'USER_MANAGEMENT.ROLE.USER',
      };
    }
  });
  protected readonly _impersonationTokenSig = this._store.selectSignal(userSelectors.impersonationToken);
  protected readonly _isAuthenticated$ = this._oidcSecurity.isAuthenticated$.pipe(
    map((authResult) => authResult.allConfigsAuthenticated.some((value) => value))
  );

  protected readonly _updateAnnouncementLoadingSig = computed(() =>
    this._store.selectSignal(appSelectors.specificLoading)().includes(LoadingIds.UPDATE_ANNOUNCEMENTS)
  );
  protected readonly _multiCountry = this._store.selectSignal(userSelectors.multipleCountries);
  protected readonly _country = this._store.selectSignal(userSelectors.country);
  protected readonly _showActions = computed(() => this._isAdminOrSuperUser() || this._impersonationTokenSig() || this._multiCountry());
  constructor(
    private _oidcSecurity: OidcSecurityService,
    private _destroy: DestroyRef,
    private _routing: AcRoutingService,
    private _dialog: MatDialog,
    private _store: Store<StoreState>,
    private _announcementApi: ApiAnnouncementService,
    private _floatingAlerts: FloatingAlertsService,
    private _dialogService: AcDialogService,
    private readonly _router: Router
  ) {}
  protected _logout(): void {
    this._store.dispatch(authActions.attemptLogout({}));
  }
  protected _goToMyAccount(): void {
    const isEnergyRehabilitationMode = this._store.selectSignal(userSelectors.isEnergyRehabilitationMode)();
    const currentUrlTree = this._router.parseUrl(this._router.url);
    const currentPath = currentUrlTree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '';
    const isRehabilitationFromQuery = currentUrlTree.queryParams['rehab'] === 'true';
    const isRehabilitationRoute = currentPath === 'consumption-invoices' || currentPath.startsWith('consumption-invoices/');

    if (isEnergyRehabilitationMode || isRehabilitationRoute || isRehabilitationFromQuery) {
      this._router.navigate(AcRoutingService.MY_ACCOUNT_ROUTE(), { queryParams: { rehab: true } });
      return;
    }
    this._routing.goToMyAccount();
  }
  public _exitImpersonation(): void {
    this._store.dispatch(userActions.setImpersonation({ impersonationToken: null }));
  }
  public _impersonate(): void {
    this._dialog.open(ImpersonationDialogComponent, {
      closeOnNavigation: true,
      disableClose: false,
    });
  }
  public _updateAnnouncements(event: MouseEvent, loading: boolean): void {
    event.stopImmediatePropagation();
    if (loading) {
      return;
    }
    this._dialogService
      .confirmationDialog('HEADER.ANNOUNCEMENTS.UPDATE')
      .pipe(
        filter((value) => !!value),
        switchMap(() => this._announcementApi.updateAnnouncements()),
        takeUntilDestroyed(this._destroy)
      )
      .subscribe((response) => {
        if (response.data) {
          this._floatingAlerts.info('HEADER.ANNOUNCEMENTS.SUCCESS', undefined, 5);
        } else {
          this._floatingAlerts.info('HEADER.ANNOUNCEMENTS.NOTHING_NEW', undefined, 5);
        }
      });
  }
  protected _changeCountry(country: 'ES' | 'PT'): void {
    // this._routing.goHome();
    this._store.dispatch(userActions.changeCountry({ country: country }));
  }

  protected _changeBusiness(): void {
    const isEnergyRehabilitationMode = this._store.selectSignal(userSelectors.isEnergyRehabilitationMode)();
    const currentUrlTree = this._router.parseUrl(this._router.url);
    const currentPath = currentUrlTree.root.children['primary']?.segments.map((segment) => segment.path).join('/') ?? '';
    const isRehabilitationFromQuery = currentUrlTree.queryParams['rehab'] === 'true';
    const isRehabilitationRoute = currentPath === 'consumption-invoices' || currentPath.startsWith('consumption-invoices/');
    const isRehabilitationContext = isEnergyRehabilitationMode || isRehabilitationRoute || isRehabilitationFromQuery;

    // si estás en rehabilitación energética → ir a home y limpiar el flag
    if (isRehabilitationContext) {
      this._store.dispatch(userActions.setEnergyRehabilitationMode({ enabled: false }));
      this._router.navigate(['/home']);
      return;
    }

    // si estás en comercializadora → ir a rehab energética y establecer el flag
    this._store.dispatch(userActions.setEnergyRehabilitationMode({ enabled: true }));
    this._router.navigate(['/consumption-invoices']);
  }

  protected readonly _showChangeBusiness = computed(() => {
    return this._country() === 'ES' && this.user.informationCommercializer && this.user.informationRehabilitation;
  });
}
